<template>
    <div>
        <div class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content" />
                </e-field>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <e-field label="Acknowledgment">
                    <e-checkbox v-model="userAccepted" :disabled="acknowledgementDisabled">
                        {{ activityContent.acknowledgment_message }}
                    </e-checkbox>
                </e-field>
            </div>
        </div>

        <div v-if="allow_student_attachments && !completed" class="columns">
            <div class="column">
                <e-field label="File Upload">
                    <edverum-file-upload
                        v-if="!completed"
                        :autosave="false"
                        :attachments="media"
                        @add="onMediaAdd"
                        @delete="onMediaDelete"
                    />
                </e-field>
            </div>
        </div>

        <activity-completion-action-row>
            <e-tooltip
                position="is-top"
                :active="submitBlockedByExistingGrade"
                :label="translate('activity_graded_no_submissions_allowed')"
                multilined
            >
                <e-button
                    v-if="canShowSubmit"
                    :loading="isLoading"
                    :disabled="disabled"
                    type="is-primary"
                    @click="submit"
                    @keyup="submit"
                >
                    Submit
                </e-button>
            </e-tooltip>
        </activity-completion-action-row>
        <Divider />
        <div v-if="!readOnly && canViewSubmissions" class="columns">
            <div class="column">
                <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading">
                    <template #content="{ submission }">
                        <div class="columns">
                            <div class="column">
                                <attachments :media="submission.submittable.media" />
                            </div>
                        </div>
                    </template>
                </submissions>
            </div>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import some from 'lodash/some.js';
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import EdverumFileUpload from '@/components/file-upload/EdverumFileUpload.vue';
import { useActivity } from '@/composables/useActivity.js';

export default {
    name: 'AcknowledgeInformation',

    components: {
        ActivityCompletionActionRow,
        Attachments,
        EdverumFileUpload,
        DisplayContent,
        Submissions,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        // `media` (this component's own mutable upload list) and `disabled` (its own
        // acknowledgment-aware formula) stay component-owned — drop them from the spread.
        const { media, disabled, ...api } = useActivity(props, ctx, {
            getPayload: () => instance.proxy.payload,
        });

        return { translate, ...api };
    },

    data: () => ({
        userAccepted: null,
        media: [],
    }),

    computed: {
        acknowledgementDisabled() {
            if (this.usedAllAttemptAuthorizations) {
                return true;
            }

            return (
                !this.userHasAuthorizedAttempt &&
                (!!this.completed || this.readOnly || !this.hasPermissionToSubmit || this.hasExistingGrade)
            );
        },

        disabled() {
            if (this.submitBlockedByExistingGrade) {
                return true;
            }

            if (this.usedAllAttemptAuthorizations) {
                return true;
            }

            return some([!this.userAccepted, this.isLate && !this.canSubmitLate && !this.userHasAuthorizedAttempt]);
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {
                    acknowledgment: this.userAccepted,
                    media: this.media,
                },
            };
        },

        allow_student_attachments() {
            return get(this.activity, 'activity_settings.allow_student_attachments', false);
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },

        hasExistingGrade: {
            immediate: true,
            handler(hasGrade) {
                if (hasGrade) {
                    this.userAccepted = true;
                }
            },
        },
    },

    methods: {
        onMediaAdd(files) {
            this.media = files;
        },

        onMediaDelete(index) {
            this.media.splice(index, 1);
        },
    },
};
</script>

<style lang="scss">
.tags {
    margin-bottom: 0;
}
</style>
